import React, { useState, useEffect, useCallback } from 'react';
import {
  EnvelopeIcon,
  UserGroupIcon,
  ArrowPathIcon,
  FunnelIcon,
  MagnifyingGlassIcon,
  ClockIcon,
  CheckCircleIcon,
  EnvelopeOpenIcon,
  ChartBarIcon,
  Cog6ToothIcon
} from '@heroicons/react/24/outline';
import {
  UserGroupIcon as UserGroupIconSolid,
  EnvelopeIcon as EnvelopeIconSolid
} from '@heroicons/react/24/solid';
import axiosInstance from '../api/axiosInstance';
import { useParams, useNavigate } from 'react-router-dom';
import BreadcrumbNavigation from './BreadcrumbNavigation';
import Button from '../ui/Button';
import { SearchInput } from '../ui/Input';
import Card, { CardHeader } from '../ui/Card';
import logger from '../../utils/logger';

const TeamInbox = ({ showNotification }) => {
  const { teamId } = useParams();
  const navigate = useNavigate();

  const [team, setTeam] = useState(null);
  const [emails, setEmails] = useState([]);
  const [stats, setStats] = useState({
    totalEmails: 0,
    unreadEmails: 0,
    emailsThisWeek: 0,
    memberCount: 0
  });
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedFolder, setSelectedFolder] = useState('inbox');
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Fetch team details and stats
  const fetchTeamData = useCallback(async () => {
    try {
      const [teamResponse, statsResponse] = await Promise.all([
        axiosInstance.get(`/api/business/teams/${teamId}`),
        axiosInstance.get(`/api/teams/${teamId}/stats`)
      ]);

      if (teamResponse.data.success) {
        setTeam(teamResponse.data.team);
      }

      if (statsResponse.data.success) {
        setStats(statsResponse.data.stats);
      }
    } catch (error) {
      logger.error('Error fetching team data:', error);
      showNotification?.('Failed to load team data', 'error');
    }
  }, [teamId, showNotification]);

  // Fetch team emails
  const fetchTeamEmails = useCallback(async (page = 1, append = false) => {
    try {
      if (page === 1) {
        setLoading(true);
      }

      const response = await axiosInstance.get(`/api/teams/${teamId}/inbox`, {
        params: {
          page,
          limit: 50,
          folder: selectedFolder,
          search: searchQuery
        }
      });

      if (response.data.success) {
        const newEmails = response.data.emails || [];

        if (append && page > 1) {
          setEmails(prev => [...prev, ...newEmails]);
        } else {
          setEmails(newEmails);
        }

        const totalPages = response.data.pagination?.pages || 1;
        setHasMore(page < totalPages);
        setCurrentPage(page);
      }
    } catch (error) {
      logger.error('Error fetching team emails:', error);
      showNotification?.('Failed to load team emails', 'error');
    } finally {
      setLoading(false);
    }
  }, [teamId, selectedFolder, searchQuery, showNotification]);

  useEffect(() => {
    fetchTeamData();
  }, [fetchTeamData]);

  useEffect(() => {
    setCurrentPage(1);
    fetchTeamEmails(1, false);
  }, [selectedFolder, searchQuery]);

  const handleRefresh = () => {
    fetchTeamData();
    fetchTeamEmails(1, false);
  };

  const handleEmailClick = (email) => {
    // Navigate to email detail view
    navigate(`/teams/${teamId}/emails/${email._id}`);
  };

  const formatDate = (date) => {
    const emailDate = new Date(date);
    const now = new Date();
    const diffInDays = Math.floor((now - emailDate) / (1000 * 60 * 60 * 24));

    if (diffInDays === 0) {
      return emailDate.toLocaleTimeString('en-US', { hour: '2-digit', minute: '2-digit' });
    } else if (diffInDays === 1) {
      return 'Yesterday';
    } else if (diffInDays < 7) {
      return emailDate.toLocaleDateString('en-US', { weekday: 'short' });
    } else {
      return emailDate.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
    }
  };

  return (
    <div className="h-full flex flex-col bg-gray-50 dark:bg-[#111111] overflow-hidden">
      {/* Header */}
      <div className="flex-shrink-0 bg-white dark:bg-[#1a1a1a] border-b border-gray-200 dark:border-gray-800 shadow-sm">
        <div className="px-6 py-4">
          {/* Title Section */}
          <div className="flex items-center justify-between mb-3">
            <div className="flex items-center space-x-3">
              <button
                onClick={() => navigate('/teams')}
                className="w-10 h-10 bg-gradient-to-br from-orange-500 to-orange-600 rounded-lg flex items-center justify-center flex-shrink-0 hover:shadow-lg transition-shadow"
              >
                <UserGroupIconSolid className="w-5 h-5 text-white" />
              </button>
              <div>
                <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
                  {team?.name || 'Team Inbox'}
                </h1>
                <p className="text-sm text-gray-600 dark:text-gray-400">
                  {team?.email || 'Loading...'}
                </p>
              </div>
            </div>

            <div className="flex items-center gap-2">
              <Button
                variant="secondary"
                size="sm"
                onClick={() => navigate(`/teams/${teamId}/settings`)}
                icon={<Cog6ToothIcon />}
              >
                Settings
              </Button>
              <Button
                variant="secondary"
                size="sm"
                onClick={handleRefresh}
                loading={loading}
                icon={<ArrowPathIcon />}
              >
                Refresh
              </Button>
            </div>
          </div>

          {/* Breadcrumbs */}
          <div className="mb-3">
            <BreadcrumbNavigation />
          </div>

          {/* Stats Cards */}
          <div className="grid grid-cols-4 gap-3">
            <Card padding="sm" className="bg-gradient-to-br from-blue-50 to-blue-100 dark:from-blue-900/20 dark:to-blue-800/20 border-blue-200 dark:border-blue-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-blue-500 rounded-lg flex items-center justify-center">
                  <EnvelopeIconSolid className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Total Emails</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.totalEmails}</p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-gradient-to-br from-orange-50 to-orange-100 dark:from-orange-900/20 dark:to-orange-800/20 border-orange-200 dark:border-orange-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-orange-500 rounded-lg flex items-center justify-center">
                  <EnvelopeOpenIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Unread</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.unreadEmails}</p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-gradient-to-br from-green-50 to-green-100 dark:from-green-900/20 dark:to-green-800/20 border-green-200 dark:border-green-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-green-500 rounded-lg flex items-center justify-center">
                  <ChartBarIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">This Week</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.emailsThisWeek}</p>
                </div>
              </div>
            </Card>

            <Card padding="sm" className="bg-gradient-to-br from-purple-50 to-purple-100 dark:from-purple-900/20 dark:to-purple-800/20 border-purple-200 dark:border-purple-800">
              <div className="flex items-center space-x-3">
                <div className="w-10 h-10 bg-purple-500 rounded-lg flex items-center justify-center">
                  <UserGroupIcon className="w-5 h-5 text-white" />
                </div>
                <div>
                  <p className="text-xs text-gray-600 dark:text-gray-400">Members</p>
                  <p className="text-lg font-bold text-gray-900 dark:text-white">{stats.memberCount}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Search and Filters */}
          <div className="mt-3 flex gap-2">
            <div className="flex-1">
              <SearchInput
                placeholder="Search team emails..."
                onSearch={setSearchQuery}
                size="sm"
              />
            </div>
            <Button
              variant="secondary"
              size="sm"
              icon={<FunnelIcon />}
            >
              Filter
            </Button>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="flex-1 overflow-hidden flex">
        {/* Sidebar - Folders */}
        <div className="w-64 bg-white dark:bg-[#1a1a1a] border-r border-gray-200 dark:border-gray-800 overflow-y-auto">
          <div className="p-4">
            <h3 className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">
              Folders
            </h3>
            <div className="space-y-1">
              {[
                { id: 'inbox', label: 'Inbox', icon: EnvelopeIcon, count: stats.unreadEmails },
                { id: 'sent', label: 'Sent', icon: EnvelopeOpenIcon },
                { id: 'archive', label: 'Archive', icon: CheckCircleIcon },
                { id: 'trash', label: 'Trash', icon: ClockIcon }
              ].map((folder) => (
                <button
                  key={folder.id}
                  onClick={() => setSelectedFolder(folder.id)}
                  className={`w-full flex items-center justify-between px-3 py-2 rounded-lg text-sm font-medium transition-colors ${
                    selectedFolder === folder.id
                      ? 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-400'
                      : 'text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-[#222222]'
                  }`}
                >
                  <div className="flex items-center space-x-2">
                    <folder.icon className="w-4 h-4" />
                    <span>{folder.label}</span>
                  </div>
                  {folder.count > 0 && (
                    <span className="px-2 py-0.5 bg-orange-500 text-white text-xs font-semibold rounded-full">
                      {folder.count}
                    </span>
                  )}
                </button>
              ))}
            </div>
          </div>
        </div>

        {/* Email List */}
        <div className="flex-1 overflow-y-auto bg-gray-50 dark:bg-[#111111]">
          <div className="max-w-4xl mx-auto p-6">
            {loading && emails.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto mb-4"></div>
                  <p className="text-gray-600 dark:text-gray-400">Loading team emails...</p>
                </div>
              </div>
            ) : emails.length === 0 ? (
              <Card className="text-center py-12">
                <EnvelopeIconSolid className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">
                  No emails found
                </h3>
                <p className="text-gray-600 dark:text-gray-400">
                  {searchQuery ? 'Try adjusting your search' : 'This folder is empty'}
                </p>
              </Card>
            ) : (
              <div className="space-y-2">
                {emails.map((email) => (
                  <Card
                    key={email._id}
                    padding="none"
                    className="hover:shadow-md transition-shadow cursor-pointer"
                    onClick={() => handleEmailClick(email)}
                  >
                    <div className="p-4">
                      <div className="flex items-start justify-between mb-2">
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center space-x-2 mb-1">
                            <h3 className={`text-sm font-medium truncate ${
                              email.read
                                ? 'text-gray-700 dark:text-gray-300'
                                : 'text-gray-900 dark:text-white font-semibold'
                            }`}>
                              {email.from?.firstName || email.from?.email || 'Unknown Sender'}
                            </h3>
                            {!email.read && (
                              <span className="w-2 h-2 bg-orange-500 rounded-full"></span>
                            )}
                          </div>
                          <p className={`text-sm truncate ${
                            email.read
                              ? 'text-gray-600 dark:text-gray-400'
                              : 'text-gray-900 dark:text-white font-medium'
                          }`}>
                            {email.subject || '(No Subject)'}
                          </p>
                          <p className="text-xs text-gray-500 dark:text-gray-500 truncate mt-1">
                            {email.message?.substring(0, 100) || 'No preview available'}
                          </p>
                        </div>
                        <div className="ml-4 flex-shrink-0 text-right">
                          <p className="text-xs text-gray-500 dark:text-gray-500">
                            {formatDate(email.receivedTime)}
                          </p>
                          {email.hasAttachments && (
                            <p className="text-xs text-gray-500 mt-1">📎</p>
                          )}
                        </div>
                      </div>

                      {/* Distribution List Indicator */}
                      <div className="flex items-center space-x-2 mt-2">
                        <span className="inline-flex items-center px-2 py-1 rounded-md text-xs font-medium bg-blue-100 text-blue-700 dark:bg-blue-900/30 dark:text-blue-400">
                          <UserGroupIcon className="w-3 h-3 mr-1" />
                          Team Email
                        </span>
                        {email.distributionList?.name && (
                          <span className="text-xs text-gray-500 dark:text-gray-500">
                            to {email.distributionList.name}
                          </span>
                        )}
                      </div>
                    </div>
                  </Card>
                ))}

                {/* Load More */}
                {hasMore && (
                  <div className="flex justify-center pt-4">
                    <Button
                      variant="ghost"
                      onClick={() => fetchTeamEmails(currentPage + 1, true)}
                      loading={loading}
                    >
                      Load More
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamInbox;
